<template>
  <Breadcrumb :items="items" class="text-primary-1-100" />
</template>

<script setup lang="ts">
defineComponent({
  name: 'ProductPageBreadcrumb',
});

const props = defineProps({
  threshold: {
    type: Number,
    default: null,
  },
});

const { product } = useProductContext();
const { data } = useAsyncData(async () => await useCategories());
const { t } = useI18n();
const excludePaths = ['brands', 'best-sellers', 'new-arrivals'];

const items = computed(() => {
  const { categories, name, url_key } = product.value || {};
  if (!categories?.length) {
    return [];
  }

  // We're intersted in categories paths only
  const exactPath = toRaw(categories)?.filter(category => {
    const path = category?.url_path?.split('/').shift();
    return !excludePaths.includes(path as string);
  });

  // Get the last one that has the full path
  const slugs = computed(() => exactPath?.pop()?.url_path?.split('/'));

  const paths = computed(
    () =>
      slugs.value?.map((slug: string) => {
        // We search for the category within the categoryTree either by slug or by name
        // because some categories have a different 'url_key' than their names
        // for example 'Perfumes' has url_key = 'fragrances'
        const category = data?.value?.findBySlug(slug) || data?.value?.findByName(slug);

        const path = slugs.value
          ?.reduce((acc: any[], s: any) => {
            if (!acc.includes(slug)) {
              acc.push(s);
            }

            return acc;
          }, [] as any[])
          .join('/');

        return {
          label: category?.name || '',
          path: `/${path}`,
        };
      }) || [],
  );

  const breadcrumbItems = computed(() =>
    [
      {
        label: t('home').toString(),
        path: '/',
      },
    ].concat(paths.value as { label: string; path: string }[]),
  );

  breadcrumbItems.value.push({
    label: name as string,
    path: `/${url_key}`,
  });

  return props.threshold ? breadcrumbItems.value.slice(0, props.threshold + 1) : breadcrumbItems.value;
});
</script>

<style lang="postcss" scoped></style>
<i18n>
{
  "en": {
    "home": "Homepage"
  },
  "ar": {
    "home": "الرئيسية"
  }
}
</i18n>
